// A placement's editable fields, shared by its section card on the survey
// page (placement-section.tsx) and, until Task 13 retires it, the old
// placements panel's form. One schema throughout: the server carries every
// setting whatever the mode (placements.rs's row has no nullable columns
// here), so only what's shown changes — the kiosk attract/reset/thanks knobs
// only mean something to a kiosk left running unattended, `revisit` only to
// an inline embed a browser can reload, and the invite expiry only to a link
// mailed out (email spec §2.2).

import { Input, NumberStepper, SegmentedControl, Select, Switch } from "@pej/kore/components";
import { z } from "zod";
import type { PatchPlacement } from "@/api/placements";
import type { Placement } from "@/api/types";
import { Field, useFormContext } from "@/lib/forms";

// 3..=300 matches placements.rs's validators for resetSeconds/thanksSeconds.
export const SECONDS_MIN = 3;
export const SECONDS_MAX = 300;
// 1..=365 matches placements.rs's validator for inviteTtlDays.
export const INVITE_TTL_MIN = 1;
export const INVITE_TTL_MAX = 365;

export const placementFormSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
    mode: z.enum(["kiosk", "inline", "link"]),
    attractEnabled: z.boolean(),
    attractText: z.string(),
    resetSeconds: z
        .number()
        .int()
        .min(SECONDS_MIN, `At least ${SECONDS_MIN} seconds`)
        .max(SECONDS_MAX, `At most ${SECONDS_MAX} seconds`),
    thanksSeconds: z
        .number()
        .int()
        .min(SECONDS_MIN, `At least ${SECONDS_MIN} seconds`)
        .max(SECONDS_MAX, `At most ${SECONDS_MAX} seconds`),
    revisit: z.enum(["show_submitted", "hide", "allow_again"]),
    inviteTtlDays: z
        .number()
        .int()
        .min(INVITE_TTL_MIN, `At least ${INVITE_TTL_MIN} day`)
        .max(INVITE_TTL_MAX, `At most ${INVITE_TTL_MAX} days`),
    active: z.boolean(),
});

export type PlacementFormValues = z.infer<typeof placementFormSchema>;

export const NEW_PLACEMENT_DEFAULTS: PlacementFormValues = {
    name: "",
    mode: "kiosk",
    attractEnabled: false,
    attractText: "",
    resetSeconds: 15,
    thanksSeconds: 15,
    revisit: "show_submitted",
    inviteTtlDays: 30,
    active: true,
};

export const MODE_LABELS: Record<PlacementFormValues["mode"], string> = {
    kiosk: "Kiosk",
    inline: "Inline",
    link: "Link",
};

export const REVISIT_OPTIONS: { value: PlacementFormValues["revisit"]; label: string }[] = [
    { value: "show_submitted", label: "Show submitted" },
    { value: "hide", label: "Hide" },
    { value: "allow_again", label: "Allow again" },
];

export function revisitLabel(value: string): string {
    return REVISIT_OPTIONS.find((option) => option.value === value)?.label ?? "Show submitted";
}

export function toPlacementDefaults(placement: Placement): PlacementFormValues {
    return {
        name: placement.name,
        mode: placement.mode,
        attractEnabled: placement.attractEnabled,
        attractText: placement.attractText ?? "",
        resetSeconds: placement.resetSeconds,
        thanksSeconds: placement.thanksSeconds,
        revisit: placement.revisit,
        inviteTtlDays: placement.inviteTtlDays,
        active: placement.active,
    };
}

export function toPlacementBody(values: PlacementFormValues): PatchPlacement {
    return {
        name: values.name,
        active: values.active,
        mode: values.mode,
        attractEnabled: values.attractEnabled,
        attractText: values.attractText.trim() || null,
        resetSeconds: values.resetSeconds,
        thanksSeconds: values.thanksSeconds,
        revisit: values.revisit,
        inviteTtlDays: values.inviteTtlDays,
    };
}

/** Name, mode, the mode's own settings, and whether it takes responses — not on a draft:
 *  POST /placements has no `active`, so a new placement is always created
 *  active and the switch appears once it exists. */
export function PlacementFields({ isDraft = false }: { isDraft?: boolean }) {
    const { control, watch } = useFormContext<PlacementFormValues>();
    const mode = watch("mode");
    const attractEnabled = watch("attractEnabled");
    return (
        <div className="flex flex-col gap-4">
            <Field name="name" control={control} label="Name" required render={({ field }) => <Input {...field} />} />
            <Field
                name="mode"
                control={control}
                label="Mode"
                description="Kiosk is a shared device that resets between guests; inline is a guest's own device — a QR code or embedded in a page; link is a survey sent to one guest, in an email."
                render={({ field: { value, onChange } }) => (
                    <SegmentedControl<PlacementFormValues["mode"]>
                        aria-label="Mode"
                        value={value}
                        onValueChange={onChange}
                        options={[
                            { value: "kiosk", label: MODE_LABELS.kiosk },
                            { value: "inline", label: MODE_LABELS.inline },
                            { value: "link", label: MODE_LABELS.link },
                        ]}
                    />
                )}
            />

            {mode === "kiosk" ? (
                <>
                    <Field
                        name="attractEnabled"
                        control={control}
                        label="Attract loop"
                        description="Show an idle attract screen between visitors."
                        render={({ field: { value, onChange } }) => (
                            <Switch checked={value} onCheckedChange={onChange} aria-label="Attract loop" />
                        )}
                    />
                    {attractEnabled ? (
                        <Field
                            name="attractText"
                            control={control}
                            label="Attract text"
                            optional
                            render={({ field }) => <Input {...field} placeholder="Tap to start" />}
                        />
                    ) : null}
                    <Field
                        name="resetSeconds"
                        control={control}
                        label="Reset after"
                        description={`Idle seconds before returning to the attract screen (${SECONDS_MIN}-${SECONDS_MAX}).`}
                        render={({ field: { value, onChange }, fieldState }) => (
                            <NumberStepper
                                value={value}
                                onChange={(next) => onChange(next === "" ? SECONDS_MIN : next)}
                                min={SECONDS_MIN}
                                max={SECONDS_MAX}
                                unit="s"
                                ariaLabel="Reset after (seconds)"
                                aria-invalid={fieldState.invalid}
                            />
                        )}
                    />
                    <Field
                        name="thanksSeconds"
                        control={control}
                        label="Thank-you duration"
                        description={`Seconds the thank-you screen stays up before resetting (${SECONDS_MIN}-${SECONDS_MAX}).`}
                        render={({ field: { value, onChange }, fieldState }) => (
                            <NumberStepper
                                value={value}
                                onChange={(next) => onChange(next === "" ? SECONDS_MIN : next)}
                                min={SECONDS_MIN}
                                max={SECONDS_MAX}
                                unit="s"
                                ariaLabel="Thank-you duration (seconds)"
                                aria-invalid={fieldState.invalid}
                            />
                        )}
                    />
                </>
            ) : mode === "link" ? (
                <Field
                    name="inviteTtlDays"
                    control={control}
                    label="Invites expire after"
                    description={`Days a mailed link stays answerable when the sender names no expiry (${INVITE_TTL_MIN}-${INVITE_TTL_MAX}).`}
                    render={({ field: { value, onChange }, fieldState }) => (
                        <NumberStepper
                            value={value}
                            onChange={(next) => onChange(next === "" ? INVITE_TTL_MIN : next)}
                            min={INVITE_TTL_MIN}
                            max={INVITE_TTL_MAX}
                            unit="d"
                            ariaLabel="Invites expire after (days)"
                            aria-invalid={fieldState.invalid}
                        />
                    )}
                />
            ) : (
                <Field
                    name="revisit"
                    control={control}
                    label="On revisit"
                    description="What a browser sees if it reloads after already submitting."
                    render={({ field: { value, onChange, name } }) => (
                        <Select value={value} onValueChange={onChange} name={name}>
                            <Select.Trigger aria-label="On revisit">
                                <Select.Value>{(v: unknown) => revisitLabel(String(v))}</Select.Value>
                            </Select.Trigger>
                            <Select.Content>
                                {REVISIT_OPTIONS.map((option) => (
                                    <Select.Item key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select>
                    )}
                />
            )}

            {!isDraft && (
                <Field
                    name="active"
                    control={control}
                    label="Active"
                    description="An inactive placement stops accepting responses."
                    render={({ field: { value, onChange } }) => (
                        <Switch checked={value} onCheckedChange={onChange} aria-label="Active" />
                    )}
                />
            )}
        </div>
    );
}
